import { ArrowRight } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import NextLink from "next/link"
import { products } from "../../constants/content"
import MarketingHeading from "../common/MarketingHeading"
import ProductCardVisual from "./ProductCardVisual"

type ProductId =
  | "explorer"
  | "api"
  | "datashare"
  | "sheets"
  | "studio"
  | "mcp"
  | "research"
  | "integrations"
  | "data-partnerships"
  | "indexes"

const coreProductIds = ["explorer", "studio", "mcp", "sheets", "api", "datashare", "research"]
const partnershipProductIds = ["data-partnerships", "integrations", "indexes"]

interface ProductCardProps {
  solution: (typeof products)[number]
  isCompact?: boolean
}

function ProductCard({ solution, isCompact = false }: ProductCardProps) {
  if (isCompact) {
    return (
      <NextLink
        href={solution.ctaHref}
        className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-bg-popover/80 border border-solid border-border-default transition-background duration-200 hover:bg-neutral-100 dark:hover:bg-bg-popover text-fg-default"
      >
        <Icon as={solution.icon} className="w-5 h-5 shrink-0 text-fg-secondary" />
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-sm font-medium">{solution.name}</span>
          <span className="text-xs text-fg-secondary">{solution.subheading}</span>
        </div>
        <Icon
          as={ArrowRight}
          className="w-4 h-4 shrink-0 text-fg-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        />
      </NextLink>
    )
  }

  return (
    <NextLink
      href={solution.ctaHref}
      className="group h-[180px] md:h-[260px] w-full xl:max-w-[300px] rounded-2xl bg-bg-popover/80 relative border border-solid border-border-default overflow-hidden transition-background duration-200 hover:bg-neutral-100 dark:hover:bg-bg-popover p-5 pt-4 text-fg-default"
    >
      <div className="flex flex-col gap-2 h-full" style={{ position: "relative" }}>
        <div className="flex flex-col gap-0.5 h-[50px] md:h-[75px]">
          <div className="flex items-center justify-between">
            <div className="text-base font-medium">{solution.name}</div>
            <Icon
              as={ArrowRight}
              className="w-4 h-4 shrink-0 text-fg-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            />
          </div>
          <p className="text-sm text-fg-secondary text-pretty">{solution.subheading}</p>
        </div>
        <div className="relative flex-1">
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              maskImage: "radial-gradient(circle at top left, black 20%, transparent 95%)",
            }}
          >
            <ClientOnlySuspense>
              <ProductCardVisual product={solution} />
            </ClientOnlySuspense>
          </div>
        </div>
      </div>
      <div className="h-[70%] opacity-85 md:h-full md:opacity-100 absolute bottom-0 right-0 w-full pointer-events-none bg-[linear-gradient(0deg,hsl(var(--bg-surface))_0%,hsl(var(--bg-surface)/0)_100%)]" />
    </NextLink>
  )
}

export const ProductsList = ({ omittedProduct }: { omittedProduct?: ProductId }) => {
  const coreProducts = products.filter(
    ({ id }) => coreProductIds.includes(id) && id !== omittedProduct,
  )
  const partnershipProducts = products.filter(
    ({ id }) => partnershipProductIds.includes(id) && id !== omittedProduct,
  )

  return (
    <div className="flex flex-col gap-2">
      {/* Core Products */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:flex xl:flex-row xl:justify-center gap-4 w-full ml-0 p-0 xl:px-5 xl:pb-5 xl:ml-[-20px] xl:w-[calc(100%+40px)]">
        {coreProducts.map((solution) => (
          <ProductCard key={solution.id} solution={solution} />
        ))}
      </div>
      {/* Partnership Products Section */}
      {partnershipProducts.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border-default" />
            <span className="text-xs text-fg-secondary uppercase tracking-wider font-medium">
              Partnerships
            </span>
            <div className="h-px flex-1 bg-border-default" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {partnershipProducts.map((solution) => (
              <ProductCard key={solution.id} solution={solution} isCompact />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const SolutionsForEveryScale = () => {
  return (
    <div className="flex flex-col gap-1 md:gap-4 max-w-full">
      <div className="flex justify-between gap-4 md:gap-10 mb-10 flex-col md:flex-row items-start md:items-end">
        <MarketingHeading
          level={1}
          className="w-full md:max-w-none text-[36px] md:text-[46px] -mb-3.5"
        >
          Solutions for every scale
        </MarketingHeading>
        <div className="text-sm w-full md:w-[400px] text-fg-secondary">
          Our products drive technical and non-technical teams to success, building off the same
          quality data.
        </div>
      </div>
      <ProductsList />
    </div>
  )
}

export default SolutionsForEveryScale
